import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useContext, useState } from 'react';
import { useLogout, useRedirect } from 'react-admin';
import { AppContext } from '../../providers/app-context-provider/app.context';
import { calcStarterTheme } from "../../providers/app-context-provider/calc-theme";
import authProvider from '../../providers/auth-provider';
import cookiesProvider from '../../providers/cookies-provider';
import { product_logo, product_subtitle, stringAvatar } from '../../utils/functions';
import { ImageButton } from '../image-button/image-button.component';

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        width: 450,
        padding: theme.spacing(2),
    },
    section: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),

    },
    avatar: {
        width: theme.spacing(7),
        height: theme.spacing(7),
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    spacer: {
        flexGrow: 1
    },
    product_subtitles: {
        marginBottom: '48px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'bolder',
        marginTop: '4px'
    },
    product_section: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        flexDirection: 'column',
    },
    divider: {
        marginBottom: theme.spacing(3)
    }
}));

export const UserContextDrawer = ({ open, onClose }) => {

    const appContext = useContext(AppContext)
    const classes = useStyles()
    const redirect = useRedirect()
    const makeLogout = useLogout()

    const [showContextList, setShowContextList] = useState(false);
    const [showModuleList, setShowModuleList] = useState(false);
    const [loadingContextChange, setLoadingContextChange] = useState(false)

    const handleChangeContext = async (contextId) => {

        setLoadingContextChange(true)
        const newContext = appContext.contexts.find(context => context.id === contextId)
        await authProvider.switchContext(newContext, appContext.contexts, appContext.product).then(
            (data) => {
                Object.assign(data, { product: appContext.product })
                appContext.setContext(data)
                setLoadingContextChange(false)
                setShowContextList(false)
                onClose()
                redirect('/')
            }
        )
    }

    const handleChangeProduct = (productId) => {
        const preferredTheme = cookiesProvider.getPreferredTheme()
        const newProduct = appContext.products.find(product => product?.id === productId)
        appContext.setProduct(newProduct)
        appContext.setTheme(calcStarterTheme(newProduct, preferredTheme))
        setShowModuleList(false);
        onClose();
        redirect('/')
    }

    const handleLogout = () => { makeLogout(); appContext.clearContext() }
    const handleRedirectMe = () => { redirect('/me'); onClose() }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            classes={{ paper: classes.drawerPaper }}
        >
            <Box>
                <Box className={classes.product_section}>
                    <Box className={classes.userInfo}>
                        <Avatar className={classes.avatar} {...stringAvatar(appContext?.currentUser?.name)} />
                        <Box>
                            <Typography variant="h6">{appContext?.currentUser?.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {appContext.currentUser?.email}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {appContext.profile?.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {appContext.institutionBranch?.institution?.name} - {appContext.institutionBranch?.name}
                            </Typography>
                        </Box>
                    </Box>
                    {appContext.contexts?.length > 1 && (
                        <Button
                            size="small"
                            variant='outlined'
                            color='primary'
                            startIcon={loadingContextChange ? <CircularProgress size={16} /> : <SwapHorizIcon />}
                            onClick={() => {
                                setShowContextList(!showContextList)
                                if (showModuleList) setShowModuleList(false)
                            }}
                        >
                            {'Alterar perfil'}
                        </Button>)
                    }
                </Box>
                {appContext.contexts?.length > 1 && (
                    <Box>
                        <Collapse in={showContextList}>
                            <List dense>
                                {appContext.contexts?.map((ctx) => ctx.id !== appContext?.id && (
                                    <ListItem
                                        button
                                        key={ctx.id}
                                        onClick={() => {
                                            handleChangeContext(ctx.id);
                                            setShowContextList(false);
                                        }}
                                    >
                                        <ListItemText
                                            primary={ctx.profile.name}
                                            secondary={`${ctx.institution_branch.institution.name} - ${ctx.institution_branch.name}`}
                                        />
                                        {loadingContextChange && <ListItemIcon><CircularProgress size={16} /></ListItemIcon>}
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Box>
                )}

                <Divider className={classes.divider} />
                {
                    appContext.products.length > 1 && <Box className={classes.product_section}>
                        <Button
                            size="small"
                            variant='outlined'
                            color='primary'
                            startIcon={<SwapHorizIcon />}
                            onClick={() => {
                                setShowModuleList(!showModuleList)
                                if (showContextList) setShowContextList(false)
                            }}
                        >
                            {'Alterar módulo'}
                        </Button>
                    </Box>
                }
                <Box>
                    <Collapse in={showModuleList}>
                        {appContext.products.map((product) => product?.id !== appContext?.product?.id && (
                            <Box>
                                <ImageButton
                                    url={product_logo(product.id)}
                                    title={product.name}
                                    width={'100%'}
                                    height={'75px'}
                                    onClick={() => handleChangeProduct(product.id)}
                                />
                                <Typography className={classes.product_subtitles}>
                                    {product_subtitle(product.id)}
                                </Typography>
                            </Box>
                        ))}
                    </Collapse>
                </Box>

                <Divider />

                <Box className={classes.buttonsContainer}>
                    <Button
                        startIcon={<AccountCircleIcon />}
                        variant="contained"
                        color="primary"
                        onClick={handleRedirectMe}
                    >
                        {'Meu perfil'}
                    </Button>

                    <Button
                        startIcon={<ExitToAppIcon />}
                        color="secondary"
                        variant="contained"
                        onClick={handleLogout}
                    >
                        {'Logout'}
                    </Button>
                </Box>
            </Box>
        </Drawer >
    );
}
